import { ENV } from 'env/env.config';

import { chatraWidget } from '3rdParty/chatraWidget';
import { ThemeDesignConfig } from 'themes/contracts';

// The site's support chat: Chatwoot when the brand has a Chatwoot website
// token (REACT_APP_CHATWOOT_TOKEN), otherwise Chatra (REACT_APP_CHATRA_ID).

export interface SupportChatVisitor {
  email?: string | null;
  name?: string | null;
  bidId?: number;
}

interface Chatwoot {
  setUser: (
    identifier: string,
    user: { email?: string; name?: string }
  ) => void;
  setCustomAttributes: (attributes: Record<string, string | number>) => void;
  setLocale: (locale: string) => void;
  toggle: (state?: 'open' | 'close') => void;
  reset: () => void;
}

declare global {
  interface Window {
    chatwootSettings?: Record<string, unknown>;
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: Chatwoot;
  }
}

const CHATWOOT_URL = (ENV.CHATWOOT_URL || 'https://app.chatwoot.com').replace(
  /\/$/,
  ''
);
const useChatwoot = Boolean(ENV.CHATWOOT_TOKEN);

let chatwootReady = false;
// what came before the widget was ready
let pendingVisitor: SupportChatVisitor = {};
let pendingReset = false;

function applyToChatwoot(chatwoot: Chatwoot, visitor: SupportChatVisitor) {
  if (visitor.email) {
    // the e-mail is the contact's identifier: the same person on any page.
    // Without Chatwoot's identity validation (identifier_hash) it is what the
    // browser says: a hint for support, not proof of who writes.
    chatwoot.setUser(visitor.email.toLowerCase(), {
      email: visitor.email,
      ...(visitor.name ? { name: visitor.name } : {}),
    });
  }
  if (visitor.bidId != null) {
    // a "bid" contact attribute (text) has to exist in Chatwoot's settings
    chatwoot.setCustomAttributes({ bid: `№${visitor.bidId}` });
  }
}

function addChatwoot(language: string) {
  if (document.getElementById('chatwoot-sdk')) {
    window.$chatwoot?.setLocale(language);
    return;
  }

  window.chatwootSettings = {
    position: 'right',
    type: 'standard',
    launcherTitle: '',
    locale: language,
  };

  window.addEventListener('chatwoot:ready', () => {
    chatwootReady = true;
    const chatwoot = window.$chatwoot;
    if (chatwoot == null) return;
    if (pendingReset) chatwoot.reset();
    applyToChatwoot(chatwoot, pendingVisitor);
    pendingVisitor = {};
    pendingReset = false;
  });

  const script = document.createElement('script');
  script.id = 'chatwoot-sdk';
  script.src = `${CHATWOOT_URL}/packs/js/sdk.js`;
  script.async = true;
  script.onload = () => {
    window.chatwootSDK?.run({
      websiteToken: ENV.CHATWOOT_TOKEN as string,
      baseUrl: CHATWOOT_URL,
    });
  };
  document.head.appendChild(script);
}

export const supportChat = {
  add(colors: ThemeDesignConfig['colors'], language: string) {
    if (useChatwoot) {
      addChatwoot(language);
    } else {
      chatraWidget.add(colors, language);
    }
  },
};

// What the support agent sees about the visitor: the e-mail and name, the bid
// they are looking at. The visitor can change it in the browser.
export const updateSupportChatVisitor = (visitor: SupportChatVisitor) => {
  if (!useChatwoot) {
    window.Chatra?.('updateIntegrationData', {
      ...(visitor.email !== undefined ? { email: visitor.email } : {}),
      ...(visitor.name !== undefined ? { name: visitor.name } : {}),
      ...(visitor.bidId != null ? { Заявка: `№${visitor.bidId}` } : {}),
    });
    return;
  }
  if (chatwootReady && window.$chatwoot) {
    applyToChatwoot(window.$chatwoot, visitor);
  } else {
    pendingVisitor = { ...pendingVisitor, ...visitor };
  }
};

// On logout: the next visitor of this browser starts a new conversation.
export const resetSupportChat = () => {
  if (!useChatwoot) {
    window.Chatra?.('updateIntegrationData', { email: null, name: null });
    return;
  }
  if (chatwootReady && window.$chatwoot) {
    window.$chatwoot.reset();
  } else {
    pendingVisitor = {};
    pendingReset = true;
  }
};

export const openSupportChat = () => {
  if (useChatwoot) {
    window.$chatwoot?.toggle('open');
  } else {
    window.Chatra?.('openChat', true);
  }
};
